import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { fetchRequirementStepsByAISystemId } from '../../../store/slices/requirementStepsSlice';
import { RequirementListStep } from './RequirementListStep';
import { SidePanel } from '../../ui/SidePanel/SidePanel';
import {
  handleOpenSidePanel,
  setSidePanelContent,
  setSidePanelTitle
} from '../../../store/slices/sidePanelSlice';
import {
  setAnnexIVFieldsState,
  setCreateAnnexIVDraftResponse
} from '../../../store/slices/documentationSlice';
import { getAnnexIVByAiSystem } from '../../../services/AnnexIVService';
import { mapAnnexContentToFields } from '../../../utils/documentationHelpers';

export function AISysRequirementList() {
  const dispatch = useAppDispatch();
  const { id: aiSystemId } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const hasOpenedAnnexDrawerRef = useRef(false);
  const { steps, loading, error } = useAppSelector(
    (state) => state.requirementSteps
  );
  const {
    createAnnexIVDraftResponse,
    createEUDeclarationResponse,
    createFRIAResponse,
    createDPIAResponse
  } = useAppSelector((state) => state.documentation);

  const { sidePanelTitle, sidePanelContent } = useAppSelector(
    (state) => state.sidePanel
  );

  useEffect(() => {
    if (aiSystemId) {
      dispatch(fetchRequirementStepsByAISystemId(aiSystemId));
    }
  }, [dispatch, aiSystemId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldOpenAnnexDrawer = params.get('openAnnexIVDrawer') === '1';

    if (
      !aiSystemId ||
      !shouldOpenAnnexDrawer ||
      hasOpenedAnnexDrawerRef.current
    ) {
      return;
    }

    hasOpenedAnnexDrawerRef.current = true;

    const openAnnexDrawer = async () => {
      try {
        const annex = await getAnnexIVByAiSystem(aiSystemId);
        dispatch(setCreateAnnexIVDraftResponse(annex));
        dispatch(setAnnexIVFieldsState(mapAnnexContentToFields(annex)));
        dispatch(setSidePanelTitle('Annex IV - Technical Documentation (Draft)'));
        dispatch(setSidePanelContent('ANNEX_IV'));
        dispatch(handleOpenSidePanel(true));
      } catch (error) {
        console.error('Failed to open Annex IV side drawer:', error);
      } finally {
        params.delete('openAnnexIVDrawer');
        navigate(
          {
            pathname: location.pathname,
            search: params.toString() ? `?${params.toString()}` : ''
          },
          { replace: true }
        );
      }
    };

    void openAnnexDrawer();
  }, [aiSystemId, dispatch, location.pathname, location.search, navigate]);

  const handleContinue = (stepId: string) => {
    console.log('Continue work on step:', stepId);
    // Handle navigation or action for the step
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-6 max-h-[calc(100vh-300px)] overflow-y-auto">
        <div className="flex flex-col text-left">
          <span className="text-zinc-900 font-medium">Requirements</span>
          <span className="text-gray-500 text-sm font-normal">
            Follow these compliance steps in sequential order.
          </span>
        </div>
        <div className="flex items-center justify-center py-8">
          <span className="text-gray-500">Loading requirement steps...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-6 max-h-[calc(100vh-300px)] overflow-y-auto">
        <div className="flex flex-col text-left">
          <span className="text-zinc-900 font-medium">Requirements</span>
          <span className="text-gray-500 text-sm font-normal">
            Follow these compliance steps in sequential order.
          </span>
        </div>
        <div className="flex items-center justify-center py-8">
          <span className="text-red-600">Error: {error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-h-[calc(100vh-300px)] overflow-y-auto">
      <div className="flex flex-col text-left">
        <span className="text-zinc-900 font-medium">Requirements</span>
        <span className="text-gray-500 text-sm font-normal">
          Follow these compliance steps in sequential order.
        </span>
      </div>

      <div className="flex flex-col gap-2">
        {steps.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No requirement steps available
          </div>
        ) : (
          steps.map((step) => (
            <RequirementListStep
              key={step.id}
              stepKey={step.key}
              stepId={step.id}
              order={step.order}
              title={step.title}
              status={step.status}
              overview={step.overview}
              locked={step.locked}
              onContinue={() => handleContinue(step.id)}
            />
          ))
        )}
      </div>

      {sidePanelContent && (
        <SidePanel
          title={sidePanelTitle}
          content={sidePanelContent}
          annexId={
            sidePanelContent === 'ANNEX_IV'
              ? createAnnexIVDraftResponse?.id
              : undefined
          }
          declarationId={
            sidePanelContent === 'EU_DECLARATION'
              ? createEUDeclarationResponse?.id
              : undefined
          }
          dpiaId={
            sidePanelContent === 'DPIA' ? createDPIAResponse?.id : undefined
          }
          friaId={
            sidePanelContent === 'FRIA' ? createFRIAResponse?.id : undefined
          }
        />
      )}
    </div>
  );
}
